import styled from '@emotion/styled';
import React, { useCallback, useMemo, useRef, useId } from 'react';

import type { LensButtonProps } from '../../types';
import {
  getColorValue,
  getFocusRing,
  getRadius,
  getSize,
  getSizeValue,
  u,
} from '../../utilities';

import Container from '../container/container';
import Tooltip, { type TooltipProps } from '../tooltip/tooltip';
import Icon from '../icon/icon';

const TabsContext = React.createContext<{ isPilledDesign?: boolean }>({});

const negativeScrollOffset = scrollOffset =>
  `calc(-1 * ${getSizeValue(scrollOffset)})`;

const Wrapper = styled.div<WrapperProps>`
  ${props =>
    props.scrollOffset &&
    `margin: 0 ${negativeScrollOffset(props.scrollOffset)};
  `};
`;

const TabsNav = styled.div<TabsNavProps>`
  --activeIndicatorHeight: 3px;

  display: flex;
  overflow: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${props =>
    props.hasBottomBorder && 'border-bottom: 1px solid var(--lns-color-border)'}

  ${props => props.scrollOffset && getSize('padding-left', props.scrollOffset)};

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    ${props => props.hasFullTabs && `flex: 1 0`};
    &:not(:first-of-type) {
      margin-left: 1rem;
    }
  }

  button {
    ${props => props.hasFullTabs && `width: 100%;`};
  }

  ::after {
    content: '';
    flex-shrink: 0;
    ${props => props.scrollOffset && getSize('width', props.scrollOffset)};
  }
`;

const TabsNavPilled = styled.div<TabsNavProps>`
  border-radius: var(--ds-radius-xlarge, var(--lns-radius-200));
  background-color: var(--lns-color-backgroundSecondary);

  padding: var(--lns-space-xsmall);

  display: flex;
  overflow: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ${props => props.scrollOffset && getSize('padding-left', props.scrollOffset)};

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 1 0;
  }

  button {
    width: 100%;
  }

  ::after {
    content: '';
    flex-shrink: 0;
    ${props => props.scrollOffset && getSize('width', props.scrollOffset)};
  }
`;

const TabWrapper = styled.button<TabWrapperProps>`
  appearance: none;
  font: inherit;
  background: transparent;
  border: 0;
  ${getRadius('medium')};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  padding: 0 0 calc(var(--lns-space-small) + var(--activeIndicatorHeight)) 0;
  position: relative;
  color: inherit;
  text-decoration: none;
  flex-shrink: 0;
  font-weight: var(--ds-font-weight-medium);
  transition: 0.6s color;
  white-space: nowrap;
  color: ${props =>
    getColorValue(
      props.isActive
        ? 'body'
        : props.disabled
          ? 'disabledContent'
          : 'bodyDimmed'
    )};
  ${props =>
    props.isActive &&
    `border-color: ${getColorValue('primary')};
  `};

  &:focus,
  &:focus-visible {
    outline: 1px solid transparent;
  }

  &:focus-visible {
    ${getFocusRing(undefined, 'inset')};
  }

  &:hover:not(:disabled) {
    color: ${getColorValue('body')};
    transition: 0.3s color;
  }

  &::after {
    bottom: 0;
    border-radius: 0;
    content: '';
    height: var(--activeIndicatorHeight);
    position: absolute;
    width: 100%;
    ${props =>
      props.isActive && `background-color: ${getColorValue('primary')}`};
  }
`;

const TabWrapperPilled = styled.button<TabWrapperProps>`
  padding: ${u(1)} 0;

  appearance: none;
  font: inherit;
  background: transparent;
  border: none;
  border-radius: var(--ds-radius-large, var(--lns-radius-175));
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  position: relative;
  color: inherit;
  text-decoration: none;
  flex-shrink: 0;
  font-weight: var(--ds-font-weight-medium);

  transition: 0.6s color;
  white-space: nowrap;
  color: ${props =>
    getColorValue(
      props.isActive
        ? 'body'
        : props.disabled
          ? 'disabledContent'
          : 'bodyDimmed'
    )};
  ${props =>
    props.isActive &&
    `background-color: ${getColorValue('background')};
     color: ${getColorValue('primary')};
  `};

  &:focus,
  &:focus-visible {
    outline: 1px solid transparent;
  }

  &:focus-visible {
    ${getFocusRing(undefined, 'inset')};
  }

  &:hover:not(:disabled) {
    color: ${getColorValue('primary')};
    transition: 0.3s color;
  }
`;

/** @deprecated Use tabs prop instead */
export const Tab = ({
  children,
  isActive,
  htmlTag = 'button',
  icon,
  isDisabled = false,
  ...props
}: TabProps &
  React.ComponentProps<typeof TabWrapper> &
  Omit<
    LensButtonProps,
    keyof TabProps & React.ComponentProps<typeof TabWrapper>
  >) => {
  const { isPilledDesign } = React.useContext(TabsContext);

  if (isPilledDesign) {
    return (
      <TabWrapperPilled
        as={htmlTag}
        isActive={isActive}
        icon={icon}
        role="tab"
        aria-selected={isActive}
        disabled={isDisabled}
        {...props}
      >
        {icon && (
          <Container htmlTag="span" paddingRight={children && 'small'}>
            <Icon icon={icon} color="currentColor" />
          </Container>
        )}
        {children}
      </TabWrapperPilled>
    );
  }

  return (
    <TabWrapper
      as={htmlTag}
      isActive={isActive}
      icon={icon}
      role="tab"
      aria-selected={isActive}
      disabled={isDisabled}
      {...props}
    >
      {icon && (
        <Container htmlTag="span" paddingRight={children && 'small'}>
          <Icon icon={icon} color="currentColor" />
        </Container>
      )}
      {children}
    </TabWrapper>
  );
};

/** @deprecated Use tabs prop instead */
const TabsDeprecated = ({
  children,
  scrollOffset,
  hasFullTabs,
  isPilledDesign,
  hasBottomBorder = false,
  ...props
}: TabsProps) => {
  const tabsContent = isPilledDesign ? (
    <TabsNavPilled
      hasFullTabs={hasFullTabs}
      scrollOffset={scrollOffset}
      role="tablist"
      {...props}
    >
      {children}
    </TabsNavPilled>
  ) : (
    <TabsNav
      hasFullTabs={hasFullTabs}
      scrollOffset={scrollOffset}
      hasBottomBorder={hasBottomBorder}
      role="tablist"
      {...props}
    >
      {children}
    </TabsNav>
  );

  if (scrollOffset) {
    return <Wrapper scrollOffset={scrollOffset}>{tabsContent}</Wrapper>;
  }

  return (
    <TabsContext.Provider value={{ isPilledDesign }}>
      {tabsContent}
    </TabsContext.Provider>
  );
};

const ConditionalTooltip = ({
  tooltipProps,
  children,
  tooltipId,
}: {
  tooltipProps: TooltipProps;
  children: React.ReactNode;
  tooltipId: string;
}) => {
  if (tooltipProps) {
    return (
      <Tooltip {...tooltipProps} tooltipId={tooltipId} tabIndex={-1}>
        {children}
      </Tooltip>
    );
  }
  return children;
};

export const TabNew = React.forwardRef<
  HTMLButtonElement,
  TabItem &
    React.ComponentProps<typeof TabWrapper> &
    Omit<
      LensButtonProps,
      keyof TabItem & React.ComponentProps<typeof TabWrapper>
    >
>(
  (
    {
      tabContent,
      tooltipProps,
      isActive,
      htmlTag = 'button',
      icon,
      isDisabled = false,
      onKeyDown,
      onClick,
      ...props
    },
    ref
  ) => {
    const { isPilledDesign } = React.useContext(TabsContext);

    const tooltipId = useId();

    const componentProps = {
      as: htmlTag,
      isActive,
      icon,
      role: 'tab',
      'aria-selected': isActive,
      disabled: isDisabled,
      tabIndex: isActive ? 0 : -1,
      'aria-describedby': tooltipProps ? tooltipId : undefined,
      onKeyDown,
      onClick,
      ref,
      ...props,
    };

    return (
      <ConditionalTooltip tooltipProps={tooltipProps} tooltipId={tooltipId}>
        {isPilledDesign ? (
          <TabWrapperPilled {...componentProps}>
            {icon ? (
              <Container htmlTag="span" paddingRight={tabContent && 'small'}>
                <Icon icon={icon} color="currentColor" />
              </Container>
            ) : null}
            {tabContent}
          </TabWrapperPilled>
        ) : (
          <TabWrapper {...componentProps}>
            {icon ? (
              <Container htmlTag="span" paddingRight={tabContent && 'small'}>
                <Icon icon={icon} color="currentColor" />
              </Container>
            ) : null}
            {tabContent}
          </TabWrapper>
        )}
      </ConditionalTooltip>
    );
  }
);

TabNew.displayName = 'TabNew';

export const TabsNew = ({
  tabs,
  scrollOffset,
  hasFullTabs,
  isPilledDesign,
  hasBottomBorder = false,
  ...props
}: TabsProps) => {
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentTabIndex: number) => {
      let newIndex = currentTabIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          newIndex =
            currentTabIndex > 0 ? currentTabIndex - 1 : tabs.length - 1;
          break;
        case 'ArrowRight':
          event.preventDefault();
          newIndex =
            currentTabIndex < tabs.length - 1 ? currentTabIndex + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      tabRefs.current[newIndex]?.focus();
    },
    [tabs.length]
  );

  const setTabRef = useCallback(
    (tabIndex: number) => (ref: HTMLButtonElement | null) => {
      tabRefs.current[tabIndex] = ref;
    },
    []
  );

  const tabsToRender = useMemo(
    () =>
      tabs.map((tabProps, index) => (
        <TabNew
          key={index}
          ref={setTabRef(index)}
          onKeyDown={e => handleKeyDown(e, index)}
          {...tabProps}
        />
      )),
    [tabs, handleKeyDown, setTabRef]
  );

  const tabsContent = useMemo(
    () =>
      isPilledDesign ? (
        <TabsNavPilled
          hasFullTabs={hasFullTabs}
          scrollOffset={scrollOffset}
          role="tablist"
          {...props}
        >
          {tabsToRender}
        </TabsNavPilled>
      ) : (
        <TabsNav
          hasFullTabs={hasFullTabs}
          scrollOffset={scrollOffset}
          hasBottomBorder={hasBottomBorder}
          role="tablist"
          {...props}
        >
          {tabsToRender}
        </TabsNav>
      ),
    [
      tabsToRender,
      hasFullTabs,
      scrollOffset,
      hasBottomBorder,
      isPilledDesign,
      props,
    ]
  );

  const wrappedContent = (
    <TabsContext.Provider value={{ isPilledDesign }}>
      {tabsContent}
    </TabsContext.Provider>
  );

  if (scrollOffset) {
    return <Wrapper scrollOffset={scrollOffset}>{wrappedContent}</Wrapper>;
  }

  return wrappedContent;
};

const Tabs = ({ children, tabs, ...props }: TabsProps) => {
  if (children && tabs) {
    // eslint-disable-next-line no-console
    console.warn(
      'Both children and tabs props are provided. Please use only one of them.'
    );
  }

  if (!children && !tabs) {
    // eslint-disable-next-line no-console
    console.error('Either children or tabs must be provided.');

    return null;
  }

  if (children) {
    return <TabsDeprecated {...props}>{children}</TabsDeprecated>;
  }

  return <TabsNew tabs={tabs} {...props} />;
};

type TabsProps = {
  scrollOffset?: number | string;
  hasFullTabs?: boolean;
  /** @deprecated Use tabs[] prop instead */
  children?: React.ReactNode;
  tabs?: TabItem[];
  isPilledDesign?: boolean;
  hasBottomBorder?: boolean;
};

type TooltipPropsForTabItem = Omit<
  TooltipProps,
  'children' | 'tooltipId' | 'tabIndex'
>;

export type TabItem = {
  tabContent: React.ReactNode;
  tooltipProps?: TooltipPropsForTabItem;
  icon?: React.ReactNode;
  htmlTag?: 'a' | 'button' | 'span';
  isActive?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
};

/** @deprecated Don't use this component directly, instead pass in tabs[] prop to <Tabs> */
type TabProps = {
  isActive?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  htmlTag?: 'a' | 'button' | 'span';
  isPilledDesign?: boolean;
  isDisabled?: boolean;
};

type TabWrapperProps = {
  isActive?: boolean;
  icon?: React.ReactNode;
  as?: string;
  isDisabled?: boolean;
};

type TabsNavProps = {
  scrollOffset?: number | string;
  hasFullTabs?: boolean;
  hasBottomBorder?: boolean;
};

type WrapperProps = {
  scrollOffset?: number | string;
};

export default Tabs;
